import flatten from 'lodash/flatten';
import { MutationType, STPageType, TargetType } from '@tencent/slide-sdk-pb-model';
import { type DeepReadonly } from 'ts-essentials';
import { RandomIDGenerator } from '../utils/id-generator';
import { PageDeltaUtils } from '../mutation/page/set-page-properties-mutation';
import { type ILayoutData, type IMasterData, type IPageData, type ISlideData, type INoteSlideData } from '../data';
import { getShapeKeyframe } from './get-shape-keyframe';
import { getTimingKeyframe } from './get-timing-keyframe';
import { getDataExtensionKeyframe } from './get-data-extension-keyframe';
import type { INotesMasterData } from '../data';
import type {
    IValidMutationData,
    IValidAddPageMutationData,
    IValidSetPagePropertiesMutationData,
    IValidSetShapePropertiesMutationData,
} from '../mutation/base';
import type { KeyframeContext } from './keyframe-context.interface';

export function getMasterKeyframe(master: IMasterData, index: number, context: KeyframeContext): IValidMutationData[] {
    const pageId = context.idFactory(master.id);
    const shapeTreeId = context.idFactory(getShapeTreeId(master));

    const addMaster: IValidAddPageMutationData = {
        type: MutationType.AddPage,
        pageId,
        slideMasterOptions: { themeId: master.themeId },
        shapeTreeId,
        index,
        pageType: STPageType.slideMaster,
    };

    const setMasterProperties: DeepReadonly<IValidSetPagePropertiesMutationData> = {
        type: MutationType.SetPageProperties,
        pageId,
        pageType: STPageType.slideMaster,
        ...PageDeltaUtils.fromMasterData(master),
    };

    const layoutKeyframes = master.layoutIdList.map((layoutId, index) =>
        context.presentation?.layoutMap[layoutId]
            ? getLayoutKeyframe(context.presentation.layoutMap[layoutId], index, context)
            : [],
    );

    return flatten([
        addMaster,
        setMasterProperties,
        getDataExtensionKeyframe(TargetType.Page, master, pageId, pageId),
        getShapeTreeKeyFrame(master, context, shapeTreeId),
        flatten(layoutKeyframes),
    ]);
}

export function getLayoutKeyframe(layout: ILayoutData, index: number, context: KeyframeContext): IValidMutationData[] {
    const pageId = context.idFactory(layout.id);
    const shapeTreeId = layout.cSld.spTree ? context.idFactory(layout.cSld.spTree.id) : RandomIDGenerator.next();

    const addLayout: IValidAddPageMutationData = {
        type: MutationType.AddPage,
        pageId,
        slideLayoutOptions: { masterId: layout.masterId },
        shapeTreeId,
        index,
        pageType: STPageType.slideLayout,
    };

    const setLayoutProperties: DeepReadonly<IValidSetPagePropertiesMutationData> = {
        type: MutationType.SetPageProperties,
        pageId,
        pageType: STPageType.slideLayout,
        ...PageDeltaUtils.fromLayoutData(layout),
    };

    return flatten([
        addLayout,
        setLayoutProperties,
        getDataExtensionKeyframe(TargetType.Page, layout, pageId, pageId),
        getShapeTreeKeyFrame(layout, context, shapeTreeId),
    ]);
}

export function getSlideKeyframe(slide: ISlideData, index: number, context: KeyframeContext): IValidMutationData[] {
    const pageId = context.idFactory(slide.id);
    const shapeTreeId = slide.cSld.spTree ? context.idFactory(slide.cSld.spTree.id) : RandomIDGenerator.next();

    const addSlide: IValidAddPageMutationData = {
        type: MutationType.AddPage,
        pageId,
        slideOptions: { layoutId: slide.layoutId },
        shapeTreeId,
        index,
        pageType: STPageType.slide,
    };

    const setSlideProperties: DeepReadonly<IValidSetPagePropertiesMutationData> = {
        type: MutationType.SetPageProperties,
        pageId,
        pageType: STPageType.slide,
        ...PageDeltaUtils.fromSlideData(slide),
    };

    const noteSlide = context.presentation?.noteSlideMap[slide.noteSlideId ?? ''];

    return flatten([
        addSlide,
        setSlideProperties,
        getDataExtensionKeyframe(TargetType.Page, slide, pageId, pageId),
        flatten(getShapeTreeKeyFrame(slide, context, shapeTreeId)),
        slide.cSld.spTree?.nvPr?.descr
            ? getShapeTreePropertiesKeyframe(pageId, shapeTreeId, slide.cSld.spTree.nvPr.descr)
            : [],
        slide.timing ? getTimingKeyframe(slide.timing, { ...context, page: slide }) : [],
        noteSlide ? getNoteSlideKeyframe(noteSlide, { ...context, page: slide }) : [],
    ]);
}

export function getShapeTreePropertiesKeyframe(
    pageId: string,
    shapeTreeId: string,
    descr: string,
): IValidSetShapePropertiesMutationData {
    const setShapeTreeProperties: IValidSetShapePropertiesMutationData = {
        type: MutationType.SetShapeProperties,
        pageId,
        shapeId: shapeTreeId,
        shapeDelta: {
            nvPr: { descr },
        },
    };

    return setShapeTreeProperties;
}

export function getNotesMasterKeyframe(notesMaster: INotesMasterData, context: KeyframeContext): IValidMutationData[] {
    const pageId = context.idFactory(notesMaster.id);
    const shapeTreeId = context.idFactory(notesMaster.cSld.spTree?.id ?? `${notesMaster.id}_spTree`);
    const addNoteSlide: IValidAddPageMutationData = {
        type: MutationType.AddPage,
        pageType: STPageType.notesMaster,
        index: 0,
        shapeTreeId,
        pageId,
        notesMasterOptions: { themeId: notesMaster.themeId },
    };
    const setNoteSlideProperties: DeepReadonly<IValidSetPagePropertiesMutationData> = {
        type: MutationType.SetPageProperties,
        pageId,
        pageType: STPageType.notesMaster,
        ...PageDeltaUtils.fromNotesMasterData(notesMaster),
    };
    return [addNoteSlide, setNoteSlideProperties, ...flatten(getShapeTreeKeyFrame(notesMaster, context, shapeTreeId))];
}

export function getNoteSlideKeyframe(
    noteSlide: INoteSlideData,
    context: KeyframeContext & { page: { id: string } },
): IValidMutationData[] {
    const pageId = context.idFactory(noteSlide.id);
    const slideId = context.idFactory(context.page.id);
    const shapeTreeId = context.idFactory(noteSlide.cSld.spTree?.id ?? `${noteSlide.id}_spTree`);
    const addNoteSlide: IValidAddPageMutationData = {
        type: MutationType.AddPage,
        pageType: STPageType.notes,
        index: 0,
        shapeTreeId,
        pageId,
        notesOptions: { slideId },
    };
    const setNoteSlideProperties: DeepReadonly<IValidSetPagePropertiesMutationData> = {
        type: MutationType.SetPageProperties,
        pageId,
        pageType: STPageType.notes,
        ...PageDeltaUtils.fromNoteSlideData(noteSlide),
    };
    return [addNoteSlide, setNoteSlideProperties, ...flatten(getShapeTreeKeyFrame(noteSlide, context, shapeTreeId))];
}

export function getNoteMasterKeyframe(notesMaster: INotesMasterData, context: KeyframeContext): IValidMutationData[] {
    const pageId = context.idFactory(notesMaster.id);
    const shapeTreeId = context.idFactory(getShapeTreeId(notesMaster));

    const addNotesMaster: IValidAddPageMutationData = {
        type: MutationType.AddPage,
        pageId,
        notesMasterOptions: { themeId: notesMaster.themeId },
        shapeTreeId,
        pageType: STPageType.notesMaster,
        index: 0,
    };

    const setNotesMasterProperties: DeepReadonly<IValidSetPagePropertiesMutationData> = {
        type: MutationType.SetPageProperties,
        pageId,
        pageType: STPageType.notesMaster,
        ...PageDeltaUtils.fromNotesMasterData(notesMaster),
    };

    return flatten([addNotesMaster, setNotesMasterProperties, getShapeTreeKeyFrame(notesMaster, context, shapeTreeId)]);
}

export function getShapeTreeKeyFrame(
    page: IPageData,
    context: KeyframeContext,
    shapeTreeId: string,
): IValidMutationData[] {
    const shapeTree = page.cSld.spTree;
    return shapeTree
        ? flatten(
              shapeTree.shapeList?.map((child, index) =>
                  getShapeKeyframe(child, { ...context, page }, shapeTreeId, index),
              ) ?? [],
          )
        : [];
}

export function getShapeTreeId(page: IPageData) {
    return page.cSld.spTree?.id ?? RandomIDGenerator.next();
}
